<template>
  <div class="play-page bg-neutral-100 min-h-screen flex flex-col">
    <!-- 顶栏 -->
    <header
      class="bg-white border-b border-neutral-200 px-4 sm:px-6 py-3 flex items-center gap-4 sticky top-0 z-10"
    >
      <button
        @click="goBack"
        class="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-neutral-600 transition-colors flex-shrink-0"
        title="返回"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <div class="flex-1 min-w-0">
        <h1 class="font-bold text-neutral-900 truncate">{{ tool ? tool.title : "互动工具" }}</h1>
        <p class="text-xs text-neutral-500 truncate">
          {{ tool ? tool.description : "加载中..." }}
        </p>
      </div>
      <button
        v-if="fileUrl"
        @click="openInNewWindow"
        class="px-3 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:border-primary hover:text-primary transition-colors text-sm flex items-center gap-1.5 flex-shrink-0"
        title="在新窗口打开"
      >
        <i class="fa fa-external-link"></i>
        <span class="hidden sm:inline">新窗口</span>
      </button>
      <button
        v-if="fileUrl"
        @click="insertToPpt"
        :disabled="pptLoading"
        class="px-4 py-2 rounded-lg bg-secondary text-white hover:bg-secondary/90 transition-colors text-sm font-medium flex items-center gap-1.5 flex-shrink-0 disabled:opacity-60"
      >
        <i class="fa" :class="pptLoading ? 'fa-spinner fa-spin' : 'fa-file-powerpoint-o'"></i>
        {{ pptLoading ? "生成中..." : "一键插入PPT" }}
      </button>
    </header>

    <!-- 主体 -->
    <main class="flex-1 relative">
      <!-- 加载中 -->
      <div
        v-if="loading"
        class="absolute inset-0 flex flex-col items-center justify-center text-neutral-400"
      >
        <i class="fa fa-spinner fa-spin text-4xl mb-4"></i>
        <p class="text-sm">正在加载互动工具...</p>
      </div>

      <!-- 错误 -->
      <div
        v-else-if="error"
        class="absolute inset-0 flex flex-col items-center justify-center"
      >
        <div class="w-16 h-16 bg-neutral-200 rounded-full flex items-center justify-center mb-4 text-neutral-400">
          <i class="fa fa-exclamation-triangle text-2xl"></i>
        </div>
        <p class="text-neutral-600 mb-4">{{ error }}</p>
        <button
          @click="goBack"
          class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
        >
          返回
        </button>
      </div>

      <!-- 互动区域 -->
      <iframe
        v-else-if="fileUrl"
        :src="fileUrl"
        class="w-full border-0"
        :style="{ height: iframeHeight }"
        allow="fullscreen; autoplay; microphone; camera"
      ></iframe>
    </main>
  </div>
</template>

<script>
import { ElMessageBox, ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";
import { getDownloadCost, isPlayEnabled } from "../utils/pointConfig";
import { isToolFree } from "../utils/downloadRule";

export default {
  name: "PlayPage",
  data() {
    return {
      toolId: "",
      tool: null,
      fileUrl: "",
      loading: true,
      error: "",
      pptLoading: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
    iframeHeight() {
      // 视口高度减去顶栏（约61px）
      return `calc(100vh - 61px)`;
    },
    playUrl() {
      return window.location.origin + "/play/" + this.toolId;
    },
    // 该工具对当前用户是否免创值（义乌教师 或 自己的作品）
    toolIsFree() {
      return isToolFree(this.tool, this.user && this.user.uid);
    },
    // 平台是否开放在线互动（后台开关，与下载独立）
    playEnabled() {
      return isPlayEnabled();
    },
  },
  created() {
    this.toolId = this.$route.params.id;
    if (!this.toolId) {
      this.error = "缺少工具ID";
      this.loading = false;
      return;
    }
    this.init();
  },
  methods: {
    async init() {
      // 访客模式：在线互动/一键PPT 需要登录
      if (!requireLogin(this, "在线互动")) return;
      // 平台已关闭在线互动
      if (!isPlayEnabled()) {
        this.error = "平台已关闭在线互动，暂无法在线使用工具";
        this.loading = false;
        return;
      }
      try {
        // 1. 获取工具详情
        const res = await this.$axios({
          url: `aitool/${this.toolId}?uid=${this.user.uid}`,
        });
        if (res.code != 200 || !res.data) {
          this.error = "工具不存在或已下架";
          this.loading = false;
          return;
        }
        this.tool = res.data;

        // 2. 获取文件地址（未购买则需确认扣创值）
        if (!this.tool.hasDownLoad) {
          try {
            await ElMessageBox.confirm(
              this.toolIsFree
                ? `确认免费在线使用工具《${this.tool.title}》`
                : `确认消耗：${getDownloadCost()}创值在线使用工具《${this.tool.title}》`,
              "在线使用",
              {
                confirmButtonText: "使用",
                cancelButtonText: "取消",
                type: "warning",
              }
            );
          } catch (e) {
            this.goBack();
            return;
          }
        }

        // mode=play：标记为"在线使用"取文件，后端只校验在线互动开关（不受下载开关影响）
        const dlRes = await this.$axios({
          url: "aitool/download/" + this.toolId,
          params: { mode: "play" },
        });
        if (dlRes.code == 200 && dlRes.data && dlRes.data.file) {
          // ZIP 工具包优先使用解压后的入口页（preview），HTML 工具直接用文件地址
          this.fileUrl = dlRes.data.preview || dlRes.data.file;
          this.loading = false;
          // 从「插入PPT」入口进入：自动生成PPT
          if (this.$route.query.ppt == "1") {
            this.insertToPpt();
          }
        } else {
          this.error = dlRes.msg || "获取工具文件失败（创值不足或文件缺失）";
          this.loading = false;
        }
      } catch (e) {
        this.error = "加载失败，请稍后重试";
        this.loading = false;
      }
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push("/");
      }
    },

    openInNewWindow() {
      if (this.fileUrl) window.open(this.fileUrl, "_blank");
    },

    // 图片 URL 转 dataURL（供 PPT 嵌入，避免跨域问题）
    async urlToDataURL(url) {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    },

    /**
     * 一键插入PPT：生成包含工具信息、互动链接与二维码的 PPT 页
     * 下载后可直接将本页复制进课件，放映时点击按钮即可打开互动工具
     */
    async insertToPpt() {
      if (!requireLogin(this, "一键插入PPT")) return;
      if (this.pptLoading || !this.tool) return;
      this.pptLoading = true;
      try {
        const [{ default: PptxGenJS }, { default: QRCode }] = await Promise.all([
          import("pptxgenjs"),
          import("qrcode"),
        ]);

        const pptx = new PptxGenJS();
        pptx.layout = "LAYOUT_16x9";
        const slide = pptx.addSlide();

        // 标题
        slide.addText(this.tool.title || "AI互动工具", {
          x: 0.5,
          y: 0.3,
          w: 9.0,
          h: 0.7,
          fontSize: 28,
          bold: true,
          color: "1D1D1F",
        });

        // 简介
        slide.addText(this.tool.description || "AI生成的互动小工具", {
          x: 0.5,
          y: 1.1,
          w: 5.6,
          h: 1.4,
          fontSize: 14,
          color: "6E6E73",
          valign: "top",
        });

        // 封面图
        if (this.tool.cover) {
          const coverData = await this.urlToDataURL(this.tool.cover);
          if (coverData) {
            slide.addImage({
              data: coverData,
              x: 0.5,
              y: 2.5,
              w: 3.2,
              h: 2.1,
            });
          }
        }

        // 打开互动工具按钮（超链接）
        slide.addText("▶  放映时点击打开互动工具", {
          shape: "roundRect",
          x: 4.1,
          y: 2.7,
          w: 3.6,
          h: 0.8,
          fontSize: 16,
          bold: true,
          color: "FFFFFF",
          fill: { color: "0071E3" },
          align: "center",
          hyperlink: { url: this.playUrl, tooltip: "打开互动工具" },
        });

        slide.addText("（需联网，在浏览器中打开互动页面）", {
          x: 4.1,
          y: 3.6,
          w: 3.6,
          h: 0.4,
          fontSize: 11,
          color: "86868B",
          align: "center",
        });

        // 二维码（扫码在手机/平板上互动）
        const qrDataUrl = await QRCode.toDataURL(this.playUrl, {
          width: 400,
          margin: 1,
          color: { dark: "#1D1D1F", light: "#FFFFFF" },
        });
        slide.addImage({ data: qrDataUrl, x: 8.0, y: 2.2, w: 1.6, h: 1.6 });
        slide.addText("扫码互动", {
          x: 8.0,
          y: 3.85,
          w: 1.6,
          h: 0.35,
          fontSize: 12,
          color: "6E6E73",
          align: "center",
        });

        // 来源
        slide.addText("来源：小义AI教育工具平台", {
          x: 0.5,
          y: 5.1,
          w: 9.0,
          h: 0.35,
          fontSize: 11,
          color: "86868B",
        });

        await pptx.writeFile({
          fileName: (this.tool.title || "AI互动工具") + ".pptx",
        });
        ElMessage.success("PPT已生成，下载后可将本页复制到课件中");
      } catch (error) {
        console.error("生成PPT失败:", error);
        ElMessage.error("生成PPT失败，请重试");
      } finally {
        this.pptLoading = false;
      }
    },
  },
};
</script>
